"use client";
import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  Box,
  InputBase,
  Stack,
  Typography,
  ButtonBase,
  LinearProgress
} from "@mui/material";
import { matchSorter } from "match-sorter";

import { observer, useStore } from "../../service/mobx";

function Tasks({ categories }) {
  const [search, setSearch] = useState("");
  const [changingPage, setChangingPage] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState(categories);
  const searchParamsChanged = useSearchParams().toString();

  useEffect(() => {
    if (search.trim() === "") {
      // If search is empty, show all categories and their tasks
      setFilteredCategories(categories);
    } else {
      // If search is not empty, filter categories
      const newFilteredCategories = categories
        .map(([categoryName, tasks]) => {
          const matchedTasks = matchSorter(tasks, search, { keys: [0] });

          return matchedTasks.length === 0
            ? null
            : [categoryName, matchedTasks];
        })
        .filter(Boolean); // Filter out the null entries (categories with no matches)

      setFilteredCategories(newFilteredCategories);
    }
  }, [search, categories]); // Re-run the effect when search or categories change

  useEffect(() => {
    setChangingPage(false);
  }, [searchParamsChanged]);

  return (
    <Stack
      spacing={3}
      p={{ compact: 0, expanded: 2 }}
      borderRadius="var(--shape-lg-start)"
      bgcolor={{
        compact: "var(--surface-container-lowest)",
        expanded: "var(--surface-container-high)"
      }}
      sx={{
        width: "100%",
        transition: "all 1s var(--motion-easing-emphasized)",
        "@media (min-width: 840px)": {
          "&:hover": {
            bgcolor: "var(--surface-variant)"
          }
        }
      }}
    >
      <Box
        sx={{
          "& .MuiLinearProgress-root": {
            opacity: changingPage ? 1 : 0
          }
        }}
      >
        <Search search={search} setSearch={setSearch} />
        <Box height={8}>
          <LinearProgress
            sx={{
              mt: 0.5,
              mx: 2.5,
              borderRadius: "var(--shape-round)",
              transition: "opacity 1s var(--motion-easing-emphasized)"
            }}
          />
        </Box>
      </Box>
      {filteredCategories.map(([category, tasks]) => (
        <Stack spacing={1} key={category}>
          <Typography variant="labelLg" color="var(--secondary-color)">
            {category}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {tasks.map(([task, svg, bgcolor, color]) => (
              <Chip
                key={task + searchParamsChanged}
                task={task}
                svg={svg}
                bgcolor={color}
                color={bgcolor}
                tasks={tasks}
                disabled={changingPage}
                setChangingPage={setChangingPage}
              />
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

export default observer(Tasks);

const Search = ({ search, setSearch }) => (
  <Box
    p={1}
    pt={1.5}
    px={2}
    className="search"
    boxShadow="var(--elevation-1)"
    borderRadius="var(--shape-round)"
    bgcolor="var(--surface-container-highest)"
    sx={{
      transition: "all 300ms var(--motion-easing-emphasized)",
      "&:hover": {
        boxShadow: "var(--elevation-2)",
        bgcolor: "var(--surface-container-highest)"
      }
    }}
  >
    <InputBase
      fullWidth
      size="small"
      value={search}
      placeholder="Search tasks"
      sx={{
        position: "relative",
        typography: "titleMd",
        color: "var(--surface-on-color)",
        ".Mui-disabled": {
          fontStyle: "italic",
          WebkitTextFillColor: "var(--secondary-on-container)"
        }
      }}
      inputProps={{ sx: { color: "var(--surface-on-color)" } }}
      onChange={event => setSearch(event.target.value)}
    />
  </Box>
);

function Chip({ task, svg, bgcolor, color, disabled, setChangingPage }) {
  const [clicked, setClicked] = useState(false);
  const searchParams = useSearchParams();
  const { bottomSheet } = useStore();
  const tasks = searchParams.getAll("task");
  const selected = tasks.includes(task);
  const colorful =
    clicked || selected || (tasks.length === 0 && disabled === false);
  const newSearchParams = new URLSearchParams(searchParams);

  if (tasks.includes(task)) {
    newSearchParams.delete("task", task);
  } else {
    newSearchParams.set("task", task);
  }

  newSearchParams.delete("page");

  return (
    <ButtonBase
      disabled={disabled}
      prefetch={null}
      href={
        newSearchParams.size === 0
          ? "/models"
          : `?${newSearchParams.toString()}`
      }
      onClick={() => {
        setClicked(true);
        setChangingPage(true);

        if (bottomSheet.expanded) {
          bottomSheet.set.expanded(false);
        }
      }}
      sx={{
        height: 32,
        border: 1,
        transition: "all 0.2s var(--motion-easing-emphasized)",
        opacity: colorful ? 1 : 0.7,
        width: "fit-content",
        borderRadius: "var(--shape-sm)", // Fully rounded ends (height / 2)
        borderColor: "var(--outline-variant)",
        bgcolor: colorful ? "var(--surface-container-low)" : "transparent",
        padding: 0, // << No padding on the container
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        overflow: "hidden", // << Clip inner content to border-radius
        textOverflow: "ellipsis",
        "&:hover": {
          opacity: 1,
          bgcolor: "var(--surface-container-color)"
        },
        "&:hover > div": {
          bgcolor
        },
        "&:hover > div > div": {
          bgcolor: color
        }
      }}
    >
      <Box
        sx={{
          width: 32, // << Make it square, matching height
          height: 32, // << Match button height
          bgcolor: colorful ? bgcolor : undefined, // Background color for the icon
          // No border radius here - it gets clipped by the parent
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {/* <Icon /> */}
        <Box
          component="div"
          sx={{
            width: 16,
            height: 16,
            bgcolor: colorful ? color : "var(--secondary-color)", // This sets the icon's color
            maskImage: `url(${svg})`,
            maskSize: "contain",
            maskRepeat: "no-repeat",
            maskPosition: "center",
            WebkitMaskImage: `url(${svg})`,
            WebkitMaskSize: "contain",
            WebkitMaskRepeat: "no-repeat",
            WebkitMaskPosition: "center"
          }}
        />
      </Box>
      {/* Text */}
      <Typography
        component="p"
        color={selected ? "var(--primary-color)" : "var(--secondary-color)"}
        variant="labelMd"
        sx={{ pl: 1, pr: 1.5, whiteSpace: "nowrap" }}
      >
        {task}
      </Typography>
    </ButtonBase>
  );
}
